/**
 * Compliance scoring (MERGE-PLAN §4.3) — ported from x365 computePostureScore /
 * scoreFramework. Pure and deterministic.
 *
 * Honesty rule (CISO framing): a control the app cannot test is a coverage gap,
 * never a pass. Per-framework coverage is expressed only over controls the tool
 * actually evaluated this run.
 */
import { CONTROLS, getControl } from './catalog';
import {
  SEVERITY_ORDER,
  SEVERITY_WEIGHT,
  type AuditSeverity,
  type ControlId,
  type FrameworkId,
} from './types';

export type AuditGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface PostureFinding {
  id: ControlId;
  title: string;
  severity: AuditSeverity;
  module: string;
  remediation: string;
}

export interface PostureScore {
  score: number;
  grade: AuditGrade;
  penalty: number;
  breakdown: Record<AuditSeverity, number>;
  findings: PostureFinding[];
}

export function gradeForScore(score: number): AuditGrade {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 55) return 'D';
  return 'F';
}

/**
 * Overall severity-weighted posture score from every failed control (a "fired"
 * finding key). Deduplicates keys; unknown keys are ignored.
 */
export function computePostureScore(failedControlIds: readonly ControlId[]): PostureScore {
  const seen = new Set<ControlId>();
  const breakdown: Record<AuditSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  const findings: PostureFinding[] = [];
  let penalty = 0;

  for (const id of failedControlIds) {
    if (seen.has(id)) continue;
    const c = getControl(id);
    if (!c) continue;
    seen.add(id);
    breakdown[c.severity] += 1;
    penalty += SEVERITY_WEIGHT[c.severity];
    findings.push({
      id: c.id,
      title: c.title,
      severity: c.severity,
      module: c.module,
      remediation: c.remediation,
    });
  }

  findings.sort(
    (a, b) =>
      SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) ||
      a.title.localeCompare(b.title),
  );

  const score = Math.max(0, Math.min(100, 100 - penalty));
  return { score, grade: gradeForScore(score), penalty, breakdown, findings };
}

export type CoverageClass = 'good' | 'warn' | 'fail';

export function coverageClass(pct: number): CoverageClass {
  if (pct >= 80) return 'good';
  if (pct >= 50) return 'warn';
  return 'fail';
}

export interface FrameworkCoverageItem {
  controlId: ControlId;
  title: string;
  citations: readonly string[];
  failed: boolean;
  severity: AuditSeverity;
}

export interface FrameworkCoverage {
  framework: FrameworkId;
  total: number;
  passed: number;
  failed: number;
  /** % of tested, mapped controls passing. */
  score: number;
  items: FrameworkCoverageItem[];
}

/**
 * Coverage for one framework over the controls that were tested this run.
 * `testedControlIds` = controls the app actually evaluated (pass or fail);
 * `failedControlIds` = the subset that failed. Controls not tested are excluded,
 * so the percentage never overstates assurance.
 */
export function scoreFramework(
  framework: FrameworkId,
  testedControlIds: readonly ControlId[],
  failedControlIds: readonly ControlId[],
): FrameworkCoverage {
  const tested = new Set(testedControlIds);
  const failed = new Set(failedControlIds);

  const items: FrameworkCoverageItem[] = CONTROLS.filter(
    (c) => tested.has(c.id) && c.frameworks[framework].length > 0,
  ).map((c) => ({
    controlId: c.id,
    title: c.title,
    citations: c.frameworks[framework],
    failed: failed.has(c.id),
    severity: c.severity,
  }));

  const total = items.length;
  const failedCount = items.filter((i) => i.failed).length;
  const passed = total - failedCount;
  const score = total > 0 ? Math.round((passed / total) * 100) : 100;

  return { framework, total, passed, failed: failedCount, score, items };
}
